import React from 'react';
import { isTeamSubscription } from '@/constants';
import { usePaymentInfo } from '@/utils/usePaymentInfo';
import WeChartIcon from '@/assets/image/vip/weChat.png';
import AlipayIcon from '@/assets/image/vip/alipay.png';
import styles from './index.module.css';
import { track } from '@/utils/common';
import { BusinessType, TabType } from '@/constants/subscription';
import { useAtom } from 'jotai';
import { businessTypeAtom, curTabAtom, visibleBusinessAtom } from '@/store';
import { useLocaleStore } from '@/i18n/localeStore';

interface PayQrContentProps {
  /** 是否展示商用授权书链接（团队版场景） */
  showCommercialLicense?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const PayQrContent: React.FC<PayQrContentProps> = ({
  showCommercialLicense = false,
  className,
  style,
}) => {
  const {
    qrCodeUrl,
    loading,
    seats,
    totalPrice,
    duration,
    unitPrice,
    payChannels,
    payChannelsText,
  } = usePaymentInfo();

  const [curTab] = useAtom(curTabAtom);
  const [, setVisibleBusiness] = useAtom(visibleBusinessAtom);
  const [, setBusinessType] = useAtom(businessTypeAtom);
  const { $t } = useLocaleStore();

  const handleTermClick = () => {
    track({
      event_id: 'vip_page_click',
      event_name: '会员落地页点击',
      params: { class: 'vip_tk' },
    });
  };

  const handleContactBusiness = () => {
    setBusinessType(BusinessType.LargeSeats);
    setVisibleBusiness(true);
  };

  return (
    <div className={`${styles.container} ${className || ''}`} style={style}>
      <div className={styles.priceSection}>
        <div
          className={styles.totalPriceLine}
          dangerouslySetInnerHTML={{
            __html: $t((v, { r }) =>
              r(v.PayAmount, [
                `<span class="${styles.totalPriceAmount}">${
                  totalPrice == null || Number.isNaN(Number(totalPrice)) ? '-' : totalPrice
                }</span>`,
              ])
            ),
          }}
        ></div>
        {isTeamSubscription && duration > 0 ? (
          <div className={styles.priceFormula}>
            {$t((v, { r }) => r(v.PriceBreakdown, [unitPrice, seats, duration]))}
          </div>
        ) : null}
      </div>

      <div className={styles.qrCodeSection}>
        {loading || !qrCodeUrl ? (
          <div className={styles.qrCodePlaceholder}>
            <div className={styles.loadingText}>{$t((v) => v.Text_LoadingPlain_Common)}</div>
          </div>
        ) : (
          <img src={qrCodeUrl} alt="Payment QR Code" className={styles.qrCode} />
        )}
      </div>

      <div className={styles.paymentMethods}>
        {payChannels.map((item) => {
          if (item === 'weixin') {
            return (
              <img key="weixin" src={WeChartIcon} alt="WeChat Pay" className={styles.payIcon} />
            );
          } else if (item === 'alipay') {
            return <img key="alipay" src={AlipayIcon} alt="Alipay" className={styles.payIcon} />;
          }
          return null;
        })}
        <span className={styles.payText}>
          {$t((v, { r }) => r(v.ScanToPay, [payChannelsText]))}
        </span>
      </div>

      <div className={styles.agreementText}>
        {$t((v) => v.PaymentAgreementHint)}
        {curTab === TabType.MEIDOU ? (
          <a
            href="https://www.designkit.cn/meidou-service"
            target="_blank"
            rel="noreferrer"
            onClick={handleTermClick}
          >
            {$t((v) => v.MeidouRechargeAgreement)}
          </a>
        ) : (
          <a
            href="https://pro.meitu.com/posterlabs/rules/global/vipServiceAgreementPlusWeb.html"
            target="_blank"
            rel="noreferrer"
            onClick={handleTermClick}
          >
            {$t((v) => v.DesignStudioServiceAgreement)}
          </a>
        )}
        {showCommercialLicense && isTeamSubscription ? (
          <a
            href="https://pro.meitu.com/posterlabs/rules/global/vipServiceAgreementPlusWeb.html"
            target="_blank"
            rel="noreferrer"
            onClick={handleTermClick}
          >
            {$t((v) => v.CommercialLicenseAgreement)}
          </a>
        ) : null}
      </div>
      {isTeamSubscription ? (
        <div className={styles.contactBusiness} onClick={handleContactBusiness}>
          {$t((v) => v.Label_ContactBusiness)}
        </div>
      ) : null}
    </div>
  );
};

export default PayQrContent;
